import { useCallback, useEffect, useState } from 'react';
import { Button, Descriptions, Drawer, Image, Input, Segmented, Table, Tag, Tabs, message } from 'antd';
import { fileUrl, post } from '../../api';

const PAGE_SIZE = 20;

const formatTime = (value) => (value ? value.replace('T', ' ').slice(0, 19) : '-');

const attrName = (item) => item.nameCn || item.nameEn || item.nameTw || item.id;

export default function Products() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchProducts = useCallback(async (current) => {
    setLoading(true);
    try {
      const payload = { page: current, size: PAGE_SIZE };
      if (filter !== 'all') {
        payload.isShelf = filter === 'on' ? 1 : 0;
      }
      if (keyword) {
        payload.keyword = keyword;
      }
      const rsp = await post('/admin/product/list', payload);
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setList(rsp.data.list || []);
      setTotal(rsp.data.total || 0);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, [filter, keyword]);

  useEffect(() => {
    fetchProducts(page);
  }, [page, fetchProducts]);

  const openDetail = async (record) => {
    setDetailLoading(true);
    try {
      const rsp = await post('/admin/product/detail', { id: record.id });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setDetail(rsp.data);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setDetailLoading(false);
    }
  };

  const changeFilter = (value) => {
    setFilter(value);
    setPage(1);
  };

  const columns = [
    {
      title: '图片',
      dataIndex: 'image',
      align: 'center',
      width: 70,
      render: (value) =>
        value ? (
          <img
            src={fileUrl(value, 480)}
            alt="商品图"
            style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }}
          />
        ) : (
          '-'
        ),
    },
    {
      title: '商品 ID',
      dataIndex: 'id',
      align: 'center',
      width: 140,
      render: (value) => <span style={{ fontFamily: 'monospace' }}>{value}</span>,
    },
    {
      title: '商品标题',
      dataIndex: 'title',
      align: 'left',
      render: (value) => value || '-',
    },
    {
      title: '分类',
      key: 'category',
      align: 'center',
      width: 160,
      render: (_, record) => {
        const names = [record.categoryName, record.secondaryCategoryName].filter(Boolean);
        return names.length ? names.join(' / ') : '-';
      },
    },
    {
      title: '系统价',
      dataIndex: 'price',
      align: 'center',
      width: 100,
      render: (value) => (value == null ? '-' : `$${Number(value).toFixed(2)}`),
    },
    {
      title: '库存',
      dataIndex: 'stock',
      align: 'center',
      width: 80,
      render: (value) => value ?? 0,
    },
    {
      title: '上架状态',
      dataIndex: 'isShelf',
      align: 'center',
      width: 90,
      render: (value) =>
        value === 1 ? <Tag color="green">已上架</Tag> : <Tag color="default">已下架</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      align: 'center',
      width: 160,
      render: formatTime,
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      width: 70,
      render: (_, record) => (
        <Button type="link" loading={detailLoading} onClick={() => openDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  const skuColumns = [
    {
      title: '封面',
      dataIndex: 'cover',
      align: 'center',
      width: 60,
      render: (value) =>
        value ? (
          <img
            src={fileUrl(value, 480)}
            alt="sku"
            style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'SKU ID',
      dataIndex: 'id',
      align: 'center',
      render: (value) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{value}</span>,
    },
    {
      title: '价格',
      dataIndex: 'price',
      align: 'center',
      width: 90,
      render: (value) => (value == null ? '-' : `$${Number(value).toFixed(2)}`),
    },
    { title: '销量', dataIndex: 'sale', align: 'center', width: 70 },
  ];

  return (
    <>
      <div className="list-toolbar">
        <div className="list-title">系统商品</div>
        <div className="list-actions">
          <Segmented
            value={filter}
            onChange={changeFilter}
            options={[
              { label: '全部', value: 'all' },
              { label: '已上架', value: 'on' },
              { label: '已下架', value: 'off' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="搜索商品 ID / 标题"
            style={{ width: 280 }}
            onSearch={(value) => {
              setKeyword(value.trim());
              setPage(1);
            }}
            onChange={(event) => {
              if (!event.target.value) {
                setKeyword('');
                setPage(1);
              }
            }}
          />
        </div>
      </div>

      <Table
        rowKey="id"
        bordered
        columns={columns}
        dataSource={list}
        loading={loading}
        className="list-table"
        tableLayout="fixed"
        scroll={{ y: 'calc(100vh - 300px)' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count) => `共 ${count} 条`,
          onChange: setPage,
        }}
      />

      <Drawer
        title="商品详情"
        width={760}
        open={!!detail}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="商品 ID" span={2}>
                <span style={{ fontFamily: 'monospace' }}>{detail.id}</span>
              </Descriptions.Item>
              <Descriptions.Item label="分类" span={2}>
                {[detail.categoryName, detail.secondaryCategoryName].filter(Boolean).join(' / ') || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="系统价">
                {detail.price == null ? '-' : `$${Number(detail.price).toFixed(2)}`}
              </Descriptions.Item>
              <Descriptions.Item label="库存">{detail.stock ?? 0}</Descriptions.Item>
              <Descriptions.Item label="上架状态">
                {detail.isShelf === 1 ? <Tag color="green">已上架</Tag> : <Tag>已下架</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="可退款">
                {detail.isRefund === 0 ? '可以' : '不可以'}
              </Descriptions.Item>
              <Descriptions.Item label="最小购买">{detail.buyMin}</Descriptions.Item>
              <Descriptions.Item label="运费">
                {detail.freightType === 1 ? `关闭（${detail.freightAmount}）` : detail.freightAmount}
              </Descriptions.Item>
              <Descriptions.Item label="税费">{detail.tax}</Descriptions.Item>
              <Descriptions.Item label="多语言">{detail.multiLangs || '-'}</Descriptions.Item>
              <Descriptions.Item label="图片已拉取">{detail.pulled === 1 ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatTime(detail.createTime)}</Descriptions.Item>
            </Descriptions>

            <h4 style={{ margin: '20px 0 10px' }}>商品图片</h4>
            {detail.images?.length ? (
              <Image.PreviewGroup>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {detail.images.map((image) => (
                    <Image
                      key={image.id}
                      width={96}
                      height={96}
                      style={{ objectFit: 'cover', borderRadius: 6 }}
                      src={fileUrl(image.fileId, 480)}
                      preview={{ src: fileUrl(image.fileId, 1080) }}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            ) : (
              <span>-</span>
            )}

            <h4 style={{ margin: '20px 0 10px' }}>商品属性</h4>
            {detail.attributes?.length ? (
              detail.attributes.map((attribute) => (
                <div key={attribute.id} style={{ marginBottom: 8 }}>
                  <span style={{ color: '#666', marginRight: 8 }}>{attrName(attribute)}：</span>
                  {attribute.values?.length
                    ? attribute.values.map((value) => (
                        <Tag key={value.id}>{attrName(value)}</Tag>
                      ))
                    : '-'}
                </div>
              ))
            ) : (
              <span>-</span>
            )}

            <h4 style={{ margin: '20px 0 10px' }}>多语言内容</h4>
            <Tabs
              items={(detail.langs || []).map((lang) => ({
                key: lang.lang,
                label: { cn: '简体中文', en: 'English', tw: '繁體中文' }[lang.lang] || lang.lang,
                children: (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{lang.name || '-'}</div>
                    <div style={{ color: '#666', whiteSpace: 'pre-wrap', maxHeight: 180, overflow: 'auto' }}>
                      {lang.description || '（无描述）'}
                    </div>
                  </>
                ),
              }))}
            />

            <h4 style={{ margin: '20px 0 10px' }}>
              SKU（共 {detail.skuTotal} 个，展示前 {detail.skus?.length || 0} 个）
            </h4>
            <Table
              rowKey="id"
              size="small"
              bordered
              columns={skuColumns}
              dataSource={detail.skus || []}
              pagination={false}
              scroll={{ y: 260 }}
            />
          </>
        )}
      </Drawer>
    </>
  );
}
